import { AutoAwesome } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { colors } from "constants/colors";
import { useGlobalAlert } from "hooks/useAlert";
import { useRevalidatePolling } from "hooks/useRevalidatePolling";
import { useEffect, useState } from "react";
import { Form, useRouteLoaderData } from "react-router-dom";
import { Inventory } from "types/inventory";

function AutoTextField(props: TextFieldProps & { aiValue: string | null | undefined }) {
  const [value, setValue] = useState(props.aiValue || props.value);
  const [touched, setTouched] = useState(false);

  const endAdornment = (
    <Tooltip
      title={
        <>
          Autofill with AI
          <br />
          This will use the AI analyzed value for the field. If the AI analyzed value is not available, the field will
          be left blank.
        </>
      }
      placement="top"
      arrow
    >
      <IconButton
        color="primary"
        disabled={false}
        onClick={() => {
          setValue(null);
          setTouched(false);
        }}
      >
        <AutoAwesome />
      </IconButton>
    </Tooltip>
  );

  return (
    <TextField
      fullWidth
      margin="dense"
      slotProps={{
        input: {
          endAdornment,
        },
        inputLabel: {
          shrink: true,
        },
      }}
      {...props}
      value={value || (!touched ? props.aiValue : "")}
      onChange={(e) => {
        setValue(e.target.value);
        setTouched(true);
      }}
    />
  );
}

const metadataError = (inventory: Inventory) => inventory.metadata?.error;
const metadataPresent = (inventory: Inventory) => !!(inventory.metadata?.serialNumber || metadataError(inventory));

export default function ReviewStep() {
  const { inventory } = useRouteLoaderData("receiving-inventory") as { inventory: Inventory };
  const { pollingStatus, start, stop } = useRevalidatePolling(2000, {
    maxAttempts: 10,
    onError: () => useGlobalAlert.alertError("Could not get autofill attributes"),
  });

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  useEffect(() => {
    if (metadataPresent(inventory)) stop();
  }, [inventory, stop]);

  return (
    <Stack component={Form} method="PATCH">
      <Stack direction="row" padding={1} spacing={1} alignItems="center" borderRadius={2}>
        {pollingStatus == "pending" && (
          <>
            <CircularProgress size={16} />
            <Typography>Analyzing images...</Typography>
          </>
        )}
        {metadataPresent(inventory) && !metadataError(inventory) && (
          <>
            <AutoAwesome />
            <Typography>Autofill ready!</Typography>
          </>
        )}
        {metadataError(inventory) && (
          <Alert severity="error" sx={{ flexGrow: 1 }}>
            <Typography>Error: Could not identify images.</Typography>
            <Typography variant="caption">{metadataError(inventory)?.message}</Typography>
          </Alert>
        )}
        {pollingStatus == "maxAttempts" && (
          <>
            <Typography color="error">Could not get autofill attributes.</Typography>
            <Button onClick={start}>retry</Button>
          </>
        )}
      </Stack>
      <AutoTextField name="serialNumber" label="Serial Number" aiValue={inventory.metadata?.serialNumber} required />
      <AutoTextField name="assetTag" label="Asset Tag" aiValue={inventory.metadata?.assetTag} />
      <AutoTextField name="model" label="Model" aiValue={inventory.metadata?.model} />
      <AutoTextField select name="color" label="Color" aiValue={inventory.metadata?.color}>
        {colors.map((color) => (
          <MenuItem key={color} value={color}>
            {color}
          </MenuItem>
        ))}
      </AutoTextField>
      <AutoTextField name="year" label="Year" aiValue="" />
      <Button type="submit" size="large" variant="outlined">
        Submit
      </Button>
    </Stack>
  );
}
